// Shared column factory for the Workflow Hygiene drill-down tables
// (PRODOPSAI-385). The three drill-down rows (unlinked items, non-spine
// statuses, non-canonical types) all carry the same five issue fields —
// issueKey, issueType, summary, createdDate, assignee — so the column
// definitions are identical apart from the row type. Extracting them
// keeps SonarCloud new-code duplication below the 3% threshold and means
// any visual change to e.g. the Key column applies everywhere.

import type { ReactNode } from 'react';
import type { DrillColumn } from './DrillTable';
import { jiraIssueUrl } from './jira-link';

/**
 * The minimal issue-row shape all three drill-down responses share. Each
 * row carries an `issueKey` that links to Jira and the standard issue
 * metadata. The factory is generic over T so callers keep their precise
 * row type (e.g. `IUnlinkedItem` vs `INonCanonicalTypeItem`) at the call
 * site rather than widening to a union.
 */
interface IssueRow {
  issueKey: string;
  issueType: string;
  summary: string;
  createdDate: string;
  assignee: string | null;
}

/**
 * Build the standard five-column set (Key · Type · Summary · Created ·
 * Assignee) for a drill-down table. Returns columns typed against `T`
 * (which must extend IssueRow) so each drill-down keeps its own row type
 * at the table-level seam.
 */
export function buildIssueColumns<T extends IssueRow>(): DrillColumn<T>[] {
  return [
    {
      key: 'issueKey',
      label: 'Key',
      className: 'font-mono text-xs whitespace-nowrap',
      render: (r): ReactNode => (
        <a
          href={jiraIssueUrl(r.issueKey)}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          {r.issueKey}
        </a>
      ),
    },
    { key: 'issueType', label: 'Type', className: 'whitespace-nowrap', render: (r) => r.issueType || '—' },
    { key: 'summary', label: 'Summary', render: (r) => r.summary || '—' },
    {
      key: 'createdDate',
      label: 'Created',
      className: 'whitespace-nowrap',
      render: (r) => r.createdDate.slice(0, 10),
    },
    { key: 'assignee', label: 'Assignee', className: 'whitespace-nowrap', render: (r) => r.assignee ?? 'Unassigned' },
  ];
}
